import {
  type AggregationsAggregate,
  type SearchResponseBody } from
'@elastic/elasticsearch/lib/api/types';
import { Comparison } from '@fpsm/cxy-components/Comparison';

import {
  BetTypeFilter,
  DateRefinement } from
'~/components/BettingInsights/components/Filters';
import ResultCardStyles from '~/components/BettingInsights/components/ResultsCard/styles/ResultCard.module.css';
import { marketMeta } from '~/components/BettingInsights/constants';
import { bettingSplitsQueryFactory } from '~/components/BettingInsights/queries/bettingSplitsQueryFactory';
import { trackBettingSplitsBetClick } from '~/components/BettingInsights/tracking';
import { type BettingSplitsESResult } from '~/components/BettingInsights/types';
import { aggregateSubeventsFromSearchResponse } from '~/components/BettingInsights/utils/aggregateSubeventsFromSearchResponse';
import { sortBetsBySequence } from '~/components/BettingInsights/utils/sortBetsBySequence';
import { Avatar } from '~/components/OddscheckerPlus/components/Avatar';
import { BetButton } from '~/components/OddscheckerPlus/components/BetButton';
import { featureFlags } from '~/providers/feature-flag-provider';
import { type Loggable } from '~/traits/Loggable';
import { type RequestContext } from '~/types/RequestContext';

import { formatDate } from './utils/formatDate';
import { formatLine } from './utils/formatLine';
import { getPercentageComparisonValues } from './utils/getPercentageComparisonValues';
import {
  BettingInsights,
  supportedCategoryGroupsIds,
  wnbaBetTypeIds,
  wnbaEventId } from
'./BettingInsightsConfig';
import { type BettingSplitsIndex } from './types';

export const bettingSplitsIndex = 'search-pbd-stats';

export function bettingSplitsConfigFactory(
this: Loggable,
requestContext: RequestContext)
{
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const context = this;
  return BettingInsights.withRequestContext<BettingSplitsIndex>(
    requestContext,
    {
      elasticSearch: {
        facetAttributes: [
        {
          attribute: `marketDisplayName`,
          field: 'marketDisplayNameMap.us',
          type: 'string'
        },
        {
          attribute: 'eventDisplayName',
          field: 'eventDisplayName',
          type: 'string'
        },
        {
          attribute: 'subeventStartDate',
          field: 'subeventStartDate',
          type: 'string'
        },
        {
          attribute: 'subeventStartTime',
          field: 'subeventStartTime',
          type: 'string'
        }],

        getBaseFilters() {
          // Temp addition to toggle filtering out certain markets of WNBA
          const allWNBA = this.featureFlags.find(
            (flag) => flag.name === featureFlags.SUBS__PLUS__ALL_WNBA
          );

          const baseFilters = allWNBA?.enabled ?
          [] :
          [
          // Temp addition to filter out certain markets of WNBA
          {
            bool: {
              must_not: [
              {
                bool: {
                  must: [
                  { term: { eventId: wnbaEventId } },
                  {
                    terms: {
                      betTypeId: wnbaBetTypeIds
                    }
                  }]

                }
              }]

            }
          }];


          const newCategoryGroupsFeature = this.featureFlags.find(
            (flag) => flag.name === featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS
          );

          if (newCategoryGroupsFeature?.enabled) return baseFilters;

          return [
          ...baseFilters,
          {
            bool: {
              should: supportedCategoryGroupsIds.map((categoryGroupId) => ({
                term: {
                  categoryGroupId
                }
              }))
            }
          }];

        },
        async beforeSearch(searchRequests) {
          return searchRequests.map(
            bettingSplitsQueryFactory({ sorting: this.sorting?.() })
          );
        },
        async afterSearch([searchRequest], [searchResponse, ...rest]) {
          const {
            request: { params }
          } = searchRequest;

          if (!params) {
            return [searchResponse, ...rest];
          }

          const { hitsPerPage = 10, page = 0 } = params || {};
          const from = !hitsPerPage || !page ? 0 : page * hitsPerPage;

          const aggregatedResponse =
          await aggregateSubeventsFromSearchResponse.call(
            context,
            this,
            searchResponse as SearchResponseBody<
              BettingSplitsESResult,
              Record<string, AggregationsAggregate>>,

            from,
            hitsPerPage
          );
          return [aggregatedResponse, ...rest];
        },
        results: {
          attributes: [
          'betName',
          'betPercentage',
          'categoryName',
          'categoryGroupId',
          'categoryGroupName',
          'categoryId',
          'categoryName',
          'eventDisplayName',
          'eventId',
          'eventDisplayName',
          'marketDisplayName',
          'subeventId',
          'subeventName',
          'subeventStartDate',
          'subeventStartTime',
          'teamData']

        },
        sorting() {
          return [
          {
            field: 'subeventStartTime',
            label: 'Event Time',
            order: 'asc'
          },
          {
            field: 'betPercentage',
            label: '% of Bets High-Low',
            order: 'desc'
          },
          {
            field: 'betPercentage',
            label: '% of Bets Low-High',
            order: 'asc'
          },
          {
            field: 'stakePercentage',
            label: '% of Money High-Low',
            order: 'desc'
          },
          {
            field: 'stakePercentage',
            label: '% of Money Low-High',
            order: 'asc'
          },
          {
            field: 'stakeBetDiff',
            label: 'Difference High-Low',
            order: 'desc'
          },
          {
            field: 'stakeBetDiff',
            label: 'Difference Low-High',
            order: 'asc'
          }];

        }
      },
      ui: {
        card: {
          table: {
            getContent({ result }) {
              const marketOrder = [
              'Point Spread',
              'Moneyline',
              'Total Points',
              'Total Runs'];

              return result.markets.sort(
                (a, b) =>
                marketOrder.indexOf(a.marketDisplayNameMap.us) -
                marketOrder.indexOf(b.marketDisplayNameMap.us)
              );
            },
            headings: [
            {
              key: 'left',
              render({ content }) {
                const { bets, marketClassification, marketGenericSequence } =
                content;
                const [leftBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                return (
                  <>
											{content.teamData?.home?.badgeUrl &&
                    content.teamData?.away?.badgeUrl &&
                    <Avatar
                      betName={content.bets[0].betName}
                      homeOrAway="away"
                      marketClassification={content.marketClassification}
                      teamData={content.teamData} />

                    }
											<p data-testid="left-bet" style={{ fontSize: 'inherit' }}>
												{leftBet.betName}
											</p>
										</>);

              },
              renderMobile({ content }) {
                const {
                  bets,
                  marketClassification,
                  marketGenericSequence,
                  teamData
                } = content;
                const [leftBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                const mobileName =
                teamData &&
                leftBet.teamType &&
                teamData[leftBet.teamType].shortName ||
                leftBet.betName;

                return (
                  <>
											{content.teamData?.home?.badgeUrl &&
                    content.teamData?.away?.badgeUrl &&
                    <Avatar
                      betName={content.bets[0].betName}
                      homeOrAway="away"
                      marketClassification={content.marketClassification}
                      teamData={content.teamData} />

                    }
											<p
                      data-testid="left-bet-mobile"
                      style={{ fontSize: 'inherit' }}>

												{mobileName}
											</p>
										</>);

              }
            },
            {
              key: 'title',
              render({ content }) {
                return (
                  <p
                    className={ResultCardStyles.mainHeading}
                    data-testid="market-header">

											{marketMeta[content.marketDisplayNameMap.us]?.heading ||
                    `${content.marketDisplayNameMap.us} (O/U)`}
										</p>);

              },
              isMain: true,
              justifyContent: 'center',
              shouldShowIndicator: true
            },
            {
              key: 'right',
              render({ content }) {
                const { bets, marketClassification, marketGenericSequence } =
                content;
                const [_, rightBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                return (
                  <>
											<p
                      data-testid="right-bet"
                      style={{ fontSize: 'inherit' }}>

												{rightBet.betName}
											</p>
											{content.teamData?.home?.badgeUrl &&
                    content.teamData?.away?.badgeUrl &&
                    <Avatar
                      betName={content.bets[0].betName}
                      homeOrAway="home"
                      marketClassification={content.marketClassification}
                      teamData={content.teamData} />

                    }
										</>);

              },
              renderMobile({ content }) {
                const {
                  bets,
                  marketClassification,
                  marketGenericSequence,
                  teamData
                } = content;
                const [_, rightBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                const mobileName =
                teamData &&
                rightBet.teamType &&
                teamData[rightBet.teamType].shortName ||
                rightBet.betName;
                return (
                  <>
											<p
                      data-testid="right-bet-mobile"
                      style={{ fontSize: 'inherit', textAlign: 'end' }}>

												{mobileName}
											</p>
											{content.teamData?.home?.badgeUrl &&
                    content.teamData?.away?.badgeUrl &&
                    <Avatar
                      betName={content.bets[0].betName}
                      homeOrAway="home"
                      marketClassification={content.marketClassification}
                      teamData={content.teamData} />

                    }
										</>);

              },
              justifyContent: 'flex-end'
            }],

            cells: [
            {
              key: 'left',
              render({
                bookmakers,
                content: {
                  bets,
                  categoryName,
                  marketClassification,
                  marketDisplayNameMap: { us: marketDisplayName },
                  marketGenericSequence,
                  marketPropType,
                  subeventName
                }
              }) {
                const [leftBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                const bookmaker = bookmakers[leftBet.bookmakerCode];

                const onClick = () => {
                  trackBettingSplitsBetClick({
                    bookmaker,
                    bet: leftBet,
                    categoryName,
                    marketDisplayName,
                    marketPropType,
                    subeventName
                  });
                };

                return (
                  <>
											<p
                      className={ResultCardStyles.buttonDescription}>
                      {`Best ${
                      marketMeta[marketDisplayName]?.info ||
                      `${leftBet.betName} Line`}`
                      }</p>
											<BetButton
                      aria-label={`Place a bet on ${leftBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${leftBet.oddsAmerican}`}
                      bet={leftBet}
                      bookieColour={bookmaker?.brandColour || 'black'}
                      bookieLogo={bookmaker?.logoSmall}
                      bookieName={bookmaker?.bookmakerName}
                      className={ResultCardStyles.oddsButton}
                      line={leftBet.line && formatLine(leftBet.line)}
                      odds={leftBet.oddsAmerican}
                      onClick={onClick} />

										</>);

              }
            },
            {
              key: 'comparison',
              render({
                content: {
                  bets,
                  marketClassification,
                  marketDisplayNameMap: { us: marketDisplayName },
                  marketGenericSequence
                }
              }) {
                const [leftBet, rightBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );

                const betInformation = `% of ${marketDisplayName}, ${leftBet.betName} v ${rightBet.betName},`;
                const betValues = getPercentageComparisonValues(
                  leftBet.betPercentage,
                  rightBet.betPercentage
                );
                const stakeValues = getPercentageComparisonValues(
                  leftBet.stakePercentage,
                  rightBet.stakePercentage
                );

                return (
                  <>
											<Comparison
                      aria-label={`${betInformation} Bets with ${betValues.leftValue}% on the left and ${betValues.rightValue}% on the right.`}
                      comparisonLabel={`% of ${marketDisplayName} Bets`}
                      tooltipText={
                      marketMeta[marketDisplayName]?.betsTooltipText
                      }
                      {...betValues} />

											<Comparison
                      aria-label={`${betInformation} stakes with ${stakeValues.leftValue}% on the left and ${stakeValues.rightValue}% on the right.`}
                      comparisonLabel={`% of ${marketDisplayName} Money`}
                      tooltipText={
                      marketMeta[marketDisplayName]?.moneyTooltipText
                      }
                      {...stakeValues} />

										</>);

              },
              isMain: true,
              isFullWidthMobile: true,
              order: -1,
              justifyContent: 'center'
            },
            {
              key: 'right',
              render({
                bookmakers,
                content: {
                  bets,
                  categoryName,
                  marketClassification,
                  marketDisplayNameMap: { us: marketDisplayName },
                  marketGenericSequence,
                  marketPropType,
                  subeventName
                }
              }) {
                const [_, rightBet] = sortBetsBySequence(
                  bets,
                  marketClassification,
                  marketGenericSequence
                );
                const bookmaker = bookmakers[rightBet.bookmakerCode];

                const onClick = () => {
                  trackBettingSplitsBetClick({
                    bookmaker,
                    bet: rightBet,
                    categoryName,
                    marketDisplayName,
                    marketPropType,
                    subeventName
                  });
                };

                return (
                  <>
											<p
                      className={ResultCardStyles.buttonDescription}>
                      {`Best ${
                      marketMeta[marketDisplayName]?.info ||
                      `${rightBet.betName} Line`}`
                      }</p>
											<BetButton
                      aria-label={`Place a bet on ${rightBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${rightBet.oddsAmerican}`}
                      bet={rightBet}
                      bookieColour={bookmaker?.brandColour || 'black'}
                      bookieLogo={bookmaker?.logoSmall}
                      bookieName={bookmaker?.bookmakerName}
                      className={ResultCardStyles.oddsButton}
                      line={rightBet.line && formatLine(rightBet.line)}
                      odds={rightBet.oddsAmerican}
                      onClick={onClick} />

										</>);

              },
              start: 3,
              justifyContent: 'flex-end'
            }],

            shouldToggle: true,
            showAdditionalTitle: true
          },
          title: {
            render({ result }) {
              return (
                <>
									<p data-testid="event-name">{result.subeventName}</p>
									<p
                    className={ResultCardStyles.headingDate}
                    data-testid="event-time">

										{' '}
										{formatDate(result.subeventStartTime)}
									</p>
								</>);

            }
          }
        },
        initialUiState() {
          return {
            hitsPerPage: 10,
            refinementList: {
              eventDisplayName: ['NFL']
            }
          };
        },
        refinements: [
        {
          attribute: 'marketDisplayName',
          label: 'Bet Type',
          render() {
            return (
              <BetTypeFilter
                attribute={this.attribute}
                isDisabled={this.isWalled}
                label={this.label}
                sortBy={['name:asc']} />);


          }
        },
        {
          attribute: 'subeventStartDate',
          label: 'Dates',
          render() {
            return (
              <DateRefinement
                attribute={this.attribute}
                isDisabled={this.isWalled}
                label={this.label} />);


          }
        }],

        results: {
          categories: {
            attribute: 'eventDisplayName',
            order: [
            'NFL',
            'NBA',
            'NCAAF',
            'NCAAB',
            'MLB',
            'WNBA',
            'EPL',
            'MLS',
            'NHL']

          },
          propsName: 'games',
          labelledBy: '#bet-name'
        },
        sorting: {
          canSort({ refinements }) {
            return (
              refinements.filter((r) => r.attribute === 'marketDisplayName').
              length > 0);

          }
        }
      }
    }
  );
}